// pages/TemplateManagement.js – Premium Blue Theme, no logic changes
import { useState, useEffect } from "react";
import { useATS } from "../context/ATSContext";
import { departmentService } from "../services/departmentService";
import { templateService } from "../services/jobService";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TemplateFormModal from "../components/TemplateFormModal";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

// Blue palette
const BLUE = {
  50: "#eff6ff",
  100: "#dbeafe",
  200: "#bfdbfe",
  300: "#93c5fd",
  400: "#60a5fa",
  500: "#3b82f6",
  600: "#2563eb",
  700: "#1d4ed8",
  800: "#1e40af",
};

// Keyframes for fade-up animation
if (
  typeof document !== "undefined" &&
  !document.getElementById("template-mgmt-keyframes")
) {
  const style = document.createElement("style");
  style.id = "template-mgmt-keyframes";
  style.textContent = `
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

const TemplateManagement = () => {
  const { store_id, userInfo } = useATS();
  const user_id = userInfo?.id;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // Names for the department ids each template carries. Fetched once so the
  // table can show where a template belongs instead of a bare id, and left
  // empty if the endpoint is unavailable — the column then reads "Unassigned"
  // rather than breaking the page.
  const [departments, setDepartments] = useState([]);
  // "all" | "unassigned" | a department id.
  const [deptFilter, setDeptFilter] = useState("all");

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await templateService.getTemplatesByStore(store_id);
      setTemplates(res.data.data || []);
      setError("");
      try {
        const deptRes = await departmentService.getDepartmentsByStore(store_id, {
          includeCounts: false,
        });
        setDepartments(deptRes?.data?.data || []);
      } catch {
        // Optional enrichment only. A template list that cannot name its
        // departments is still a usable template list.
        setDepartments([]);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (store_id !== null && store_id !== undefined) fetchTemplates();
  }, [store_id]);

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Delete this template? All associated jobs will lose template link.",
      )
    )
      return;
    try {
      await templateService.deleteTemplate(id);
      fetchTemplates();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingTemplate(null);
  };

  const handleSaved = () => {
    fetchTemplates();
  };

  const deptNameFor = (id) =>
    departments.find((dept) => Number(dept.id) === Number(id))?.name || null;

  const visibleTemplates = templates.filter((tpl) => {
    if (deptFilter === "all") return true;
    if (deptFilter === "unassigned") return !tpl.department_id;
    return Number(tpl.department_id) === Number(deptFilter);
  });

  const unassignedCount = templates.filter((tpl) => !tpl.department_id).length;

  if (store_id === null || store_id === undefined)
    return <Typography>Please log in</Typography>;

  return (
    <Box
      sx={{
        fontFamily: fontFam,
        minHeight: "100vh",
        animation: "fadeUp 0.4s ease both",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#ffff",
          }}
        >
          Job Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
          sx={{
            borderRadius: "40px",
            textTransform: "none",
            fontWeight: 600,
            color: "#fff",
            background: "#067483 ",
            boxShadow: `0 4px 12px ${alpha(BLUE[500], 0.3)}`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 8px 20px ${("#0c8a9b", 0.4)}`,
            },
            transition: "all 0.2s ease",
          }}
        >
          Create Template
        </Button>
      </Box>

      {/* Where a template belongs is now a property of it, so the library needs
          a way to ask "show me one department's" — and, more usefully, "show me
          the ones nobody has placed yet". */}
      {departments.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 2.5,
          }}
        >
          {[
            { value: "all", label: `All · ${templates.length}` },
            ...(unassignedCount
              ? [{ value: "unassigned", label: `Unassigned · ${unassignedCount}` }]
              : []),
            ...departments.map((dept) => ({
              value: String(dept.id),
              label: dept.name,
            })),
          ].map((chip) => {
            const active = deptFilter === chip.value;
            return (
              <Chip
                key={chip.value}
                label={chip.label}
                onClick={() => setDeptFilter(chip.value)}
                sx={{
                  fontFamily: fontFam,
                  fontWeight: 600,
                  fontSize: 12.5,
                  cursor: "pointer",
                  bgcolor: active ? BLUE[600] : "#fff",
                  color: active ? "#fff" : "#475569",
                  border: `1px solid ${active ? BLUE[600] : "#e2e8f0"}`,
                  "&:hover": { bgcolor: active ? BLUE[700] : BLUE[50] },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: BLUE[600] }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: "16px" }}>
          {error}
        </Alert>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            border: `1px solid ${BLUE[100]}`,
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: BLUE[50] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontFamily: fontFam }}>
                  Template Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontFamily: fontFam }}>
                  Job Title
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontFamily: fontFam }}>
                  Department · Level
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontFamily: fontFam }}>
                  Questions
                </TableCell>
                {/* Whether a JD is attached. Worth a column of its own rather
                    than only living inside the edit form: "did that upload
                    actually save?" is otherwise a question you can only answer
                    by opening every template one at a time. */}
                <TableCell sx={{ fontWeight: 700, fontFamily: fontFam }}>
                  JD
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontFamily: fontFam }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontFamily: fontFam }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTemplates.map((tpl, idx) => (
                <TableRow
                  key={tpl.id}
                  sx={{
                    transition: "background 0.2s",
                    "&:hover": { backgroundColor: BLUE[50] },
                    animation: `fadeUp 0.3s ease ${idx * 0.05}s both`,
                  }}
                >
                  <TableCell sx={{ fontFamily: fontFam, fontWeight: 500 }}>
                    {tpl.template_name}
                  </TableCell>
                  <TableCell sx={{ fontFamily: fontFam }}>
                    {tpl.title}
                  </TableCell>
                  <TableCell sx={{ fontFamily: fontFam }}>
                    {tpl.department_id ? (
                      <Box>
                        <Typography
                          sx={{ fontFamily: fontFam, fontSize: 13.5 }}
                        >
                          {deptNameFor(tpl.department_id) || "Department"}
                        </Typography>
                        {tpl.dept_node_name && (
                          <Typography
                            sx={{
                              fontFamily: fontFam,
                              fontSize: 11.5,
                              color: "#94a3b8",
                            }}
                          >
                            {tpl.dept_node_name}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Chip
                        label="Unassigned"
                        size="small"
                        sx={{
                          fontFamily: fontFam,
                          fontWeight: 600,
                          fontSize: 11,
                          height: 22,
                          bgcolor: "#f1f5f9",
                          color: "#64748b",
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: fontFam }}>
                    {tpl.questions?.length || 0}
                  </TableCell>
                  <TableCell sx={{ fontFamily: fontFam }}>
                    {tpl.jd_pdf_url ? (
                      <Box
                        component="a"
                        href={tpl.jd_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={tpl.jd_pdf_name || "Job description (PDF)"}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          maxWidth: 170,
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "#0c8a9b",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        <PictureAsPdfOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tpl.jd_pdf_name || "PDF"}
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        sx={{ fontFamily: fontFam, fontSize: 12.5, color: "#cbd5e1" }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tpl.status}
                      size="small"
                      sx={{
                        bgcolor:
                          tpl.status === "ACTIVE" ? "#f0fdf4" : "#fef2f2",
                        color: tpl.status === "ACTIVE" ? "#16a34a" : "#dc2626",
                        fontWeight: 600,
                        borderRadius: "20px",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEdit(tpl)}
                      sx={{ color: BLUE[600] }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(tpl.id)}
                      sx={{ color: "#ef4444" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {visibleTemplates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {deptFilter === "all"
                        ? 'No templates yet. Click "Create Template".'
                        : "No templates match this filter."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TemplateFormModal
        open={modalOpen}
        onClose={handleModalClose}
        template={editingTemplate}
        store_id={store_id}
        user_id={user_id}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default TemplateManagement;
